import React from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business as WorkspaceIcon,
  Add as AddIcon,
  PersonAdd as GrantIcon,
  PersonRemove as RevokeIcon,
  DriveFileRenameOutline as RenameIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Workspace { id: string; name: string; is_home: boolean }
interface Person { id: string; email: string; name: string; role: string; is_active?: number }

/**
 * Create workspaces, and decide who can work in the current one.
 *
 * Lives on the Users page because it answers the same question — who has access to what —
 * and because both are admin-only. The switcher in the top bar changes which workspace you
 * are *in*; this changes which workspaces *exist* and who may enter them.
 *
 * Membership is managed for the workspace you are currently in, so granting access to
 * HorizonXL means switching to HorizonXL first. That is deliberate: an admin cannot hand out
 * access to a workspace they are not themselves a member of.
 */
export const WorkspaceAdmin: React.FC = () => {
  const { token } = useAuth();
  const [workspaces, setWorkspaces] = React.useState<Workspace[]>([]);
  const [active, setActive] = React.useState<string | null>(null);
  const [members, setMembers] = React.useState<Person[]>([]);
  const [available, setAvailable] = React.useState<Person[]>([]);
  const [newName, setNewName] = React.useState('');
  const [renaming, setRenaming] = React.useState(false);
  const [renameTo, setRenameTo] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  const headers = React.useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const load = React.useCallback(async () => {
    try {
      const [ws, mem] = await Promise.all([
        fetch(`${API_URL}/api/workspaces`, { headers }).then(r => (r.ok ? r.json() : null)),
        fetch(`${API_URL}/api/workspaces/members`, { headers }).then(r => (r.ok ? r.json() : null)),
      ]);
      if (ws) {
        setWorkspaces(ws.workspaces || []);
        setActive(ws.active || null);
        // The top-bar switcher lives in the shared frame, which no longer remounts on navigation,
        // so tell it the list may have changed (created, renamed or deleted here)
        window.dispatchEvent(new Event('shoutvector:workspaces-changed'));
      }
      if (mem) {
        setMembers(mem.members || []);
        setAvailable(mem.available || []);
      }
    } catch {
      setError('Could not load workspaces.');
    }
  }, [headers]);

  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/workspaces`, {
        method: 'POST', headers, body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not create that workspace'); return; }
      setNewName('');
      setNotice(`Created ${data.name}. Switch to it from the bar at the top, then connect its accounts.`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const setMember = async (person: Person, member: boolean) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/workspaces/members/${person.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ member }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not change that access'); return; }
      setNotice(`${person.name || person.email} ${member ? 'can now work in' : 'no longer has access to'} this workspace.`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const rename = async () => {
    const name = renameTo.trim();
    if (!name || !active) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/workspaces/${active}`, {
        method: 'PATCH', headers, body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not rename that workspace'); return; }
      setRenaming(false);
      setNotice(`Renamed to ${data.name}.`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (w: Workspace) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/workspaces/${w.id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not delete that workspace'); return; }
      setNotice(`Deleted ${w.name}.`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const current = workspaces.find(w => w.id === active);

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WorkspaceIcon color="action" />
        <Typography variant="h6">Workspaces</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each workspace has its own social connections, posts, history and analytics. Nothing is
        shared between them.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
        {workspaces.map(w => (
          <Chip
            key={w.id}
            label={w.name}
            color={w.id === active ? 'primary' : 'default'}
            variant={w.id === active ? 'filled' : 'outlined'}
            // Only ever offered on a workspace you are not in. The server additionally refuses
            // any workspace that still holds connections, posts, media or keys, so the worst
            // this can do is remove one created by mistake.
            onDelete={w.id === active || w.is_home ? undefined : () => remove(w)}
            deleteIcon={<DeleteIcon />}
            disabled={busy}
          />
        ))}
        {/* The installer names the first workspace "Default Team", which is nobody's company.
            Renaming matters as soon as there is a second one to tell it apart from. */}
        {current && !renaming && (
          <Tooltip title={`Rename ${current.name}`}>
            <IconButton size="small" onClick={() => { setRenameTo(current.name); setRenaming(true); }}>
              <RenameIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {renaming && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label={`Rename ${current?.name || 'workspace'}`}
            value={renameTo}
            onChange={e => setRenameTo(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') rename(); }}
            disabled={busy}
            sx={{ flexGrow: 1, minWidth: 220 }}
          />
          <Button variant="contained" onClick={rename} disabled={busy || !renameTo.trim()}>Save</Button>
          <Button onClick={() => setRenaming(false)} disabled={busy}>Cancel</Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="New workspace name"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') create(); }}
          disabled={busy}
          sx={{ flexGrow: 1, minWidth: 220 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={create} disabled={busy || !newName.trim()}>
          Create
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Who can work in {current?.name || 'this workspace'}
      </Typography>
      {/* Stated plainly: the alternative is an admin wondering why granting access to another
          workspace from here silently applied to the wrong one. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        This applies to the workspace you are currently in. To manage another, switch to it first.
      </Typography>

      <List dense>
        {members.map(p => (
          <ListItem
            key={p.id}
            secondaryAction={
              <Tooltip title="Remove access to this workspace">
                <span>
                  <IconButton edge="end" disabled={busy} onClick={() => setMember(p, false)}>
                    <RevokeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            }
          >
            <ListItemText primary={p.name || p.email} secondary={p.email} />
          </ListItem>
        ))}
      </List>

      {available.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
            Can be given access
          </Typography>
          <List dense>
            {available.map(p => (
              <ListItem
                key={p.id}
                secondaryAction={
                  <Tooltip title="Give access to this workspace">
                    <span>
                      <IconButton edge="end" disabled={busy} onClick={() => setMember(p, true)}>
                        <GrantIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemText primary={p.name || p.email} secondary={p.email} />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Paper>
  );
};
